import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import { GlobalState } from './global';
import { MobileBridge } from './modules/mobile/helper/bridge';
import { installZynoTalkRouteHandoff } from './modules/talk/zynotalk-handoff';
import { DataCache } from './shared/data/cache';
import { hasPermission } from './shared/helpers/permissions.helpers';
import { Auth } from './shared/services/auth.service';
import { Logger } from './shared/services/logger';

export function useAppRouter() {
    const router = useRouter();

    const routeTransition = ref<'scale' | ''>('scale');
    router.beforeEach(async (to, from) => {
        if (from.path.substring(1).replace(/\/.*$/, '') === to.path.substring(1).replace(/\/.*$/, '')) {
            routeTransition.value = '';
        } else {
            routeTransition.value = 'scale';
        }
    });

    // In the ZynoSuite mobile app, Talk routes open the ZynoTalk app instead.
    installZynoTalkRouteHandoff(router);

    const isRouterReady = ref(false);
    router.isReady().then(() => {
        isRouterReady.value = true;
    });

    const routeExists = computed(() => router.currentRoute.value.matched.length > 0);
    const isPublicRoute = computed(() => router.currentRoute.value.meta.public);

    const routerViewKey = computed(() => {
        return Auth.tenantId.value ?? 'unauthed';
    });

    return { isPublicRoute, isRouterReady, routeExists, routeTransition, routerViewKey };
}

export function useUrlTenantRouter() {
    const log = Logger.createScoped('useUrlTenantRouter');
    const tenantIdFromHash = () => location.hash.match(/(?:^#|;)tid:([0-9a-f-]{36})/)?.[1] || null;

    const urlTenantId = ref(tenantIdFromHash());
    window.addEventListener('hashchange', () => {
        urlTenantId.value = tenantIdFromHash();
    });
    const removeUrlTenantId = () => {
        setTimeout(() => {
            location.hash = location.hash.replace(/#tid:([0-9a-f-]{36})/, '');
            log.info('Removed tenantId from URL hash');
        }, 250);
    };

    const isTenantRouted = computed(() => urlTenantId.value === null);
    if (!isTenantRouted.value) {
        log.info('Detected tenantId in URL hash', { tenantId: urlTenantId.value });
    }

    const router = useRouter();

    watch(
        [GlobalState.user, GlobalState.tenant],
        async () => {
            if (!urlTenantId.value) return;
            if (!GlobalState.user.value || !GlobalState.tenant.value) return;

            // Inside a ZynoSuite mobile app that reports its tenant, the native shell owns it (it signs
            // every request): switching here would only relabel the SPA, not change the data.
            if (MobileBridge.config.value?.tenantId) {
                log.info('Ignoring URL tenantId; the mobile app owns the tenant', { tenantId: urlTenantId.value });
                return removeUrlTenantId();
            }

            if (GlobalState.tenant.value?.id === urlTenantId.value) {
                log.info('Already on tenant from URL tenantId', { tenantId: urlTenantId.value });
                return removeUrlTenantId();
            }

            const matchingTenant = GlobalState.user.value.tenants.find(t => t.id === urlTenantId.value);
            if (matchingTenant) {
                log.info('Routing to tenant from URL tenantId', { tenantId: urlTenantId.value });
                Auth.setTenant(matchingTenant);
                removeUrlTenantId();
                return;
            }

            const hasMspAccess = hasPermission('msp', 'login') || GlobalState.user.value?.isSysAdmin || !!Auth.impersonatorTenantId.value;
            if (hasMspAccess) {
                const mspTenantsRef = DataCache.getLazyRef('mspTenants');
                await mspTenantsRef.load?.();
                const mspTenant = mspTenantsRef.value?.find(t => t.id === urlTenantId.value);
                if (mspTenant) {
                    log.info('Routing to MSP tenant from URL tenantId', { tenantId: urlTenantId.value });
                    if (Auth.impersonatorTenantId.value) Auth.clearImpersonatedTenant();
                    Auth.setImpersonatedTenantId(mspTenant.id);
                    Auth.setTenant({ ...mspTenant, isAdmin: true });
                    await Auth.reloadExistingSessionForTenantSwitch(router);
                    removeUrlTenantId();
                    return;
                }
            }

            log.warn('No matching tenant for URL tenantId', { tenantId: urlTenantId.value });
            removeUrlTenantId();
        },
        { immediate: true }
    );

    return { isTenantRouted };
}
